import { metrics } from '@seek/metrics-js';
import { useTranslations } from '@vocab/react';
import {
  Button,
  IconArrow,
  Inline,
  Stack,
  IconChevron,
  Box,
  ButtonIcon,
} from 'braid-design-system';
import clsx from 'clsx';
import { useCallback, useEffect, useState } from 'react';

import { usePrevious } from 'src/modules/hooks/usePrevious';
import { wait } from 'src/modules/utils';

import translations from './.vocab';

import * as styles from './Carousel.css';

interface Item {
  id: string;
  [key: string]: any;
}

interface ItemState {
  index: number;
  item: Item;
}

export interface Props {
  items: Item[];
  component: React.ComponentType<any>;
  nextButton?: 'button' | 'icon';
  prevButton?: 'button' | 'icon';
}

const getNextItemState = (items: Item[], activeIndex: number) => {
  const index = items?.[activeIndex + 1] ? activeIndex + 1 : 0;
  const item = items[index];

  return { index, item };
};

const getPrevItemState = (items: Item[], activeIndex: number) => {
  const index = items?.[activeIndex - 1] ? activeIndex - 1 : 0;
  const item = items[index];

  return { index, item };
};

const noop = () => {};

export const Carousel = ({
  items,
  component: CurrentItem,
  nextButton = 'button',
  prevButton,
}: Props) => {
  const itemCount = items.length;
  const hasItems = itemCount !== 0;

  const { t } = useTranslations(translations);
  const [animate, setAnimate] = useState(false);
  const [animatePrev, setAnimatePrev] = useState(false);
  const [active, setActive] = useState<ItemState | undefined>(
    hasItems ? { index: 0, item: items[0] } : undefined,
  );
  const [next, setNext] = useState<ItemState | undefined>(
    hasItems ? getNextItemState(items, 0) : undefined,
  );

  const [prev, setPrev] = useState<ItemState | undefined>(
    hasItems ? getPrevItemState(items, 0) : undefined,
  );

  const setNewCarouselState = useCallback(
    (newActiveIndex: number) => {
      setAnimate(false);
      setAnimatePrev(false);

      if (hasItems) {
        const newActiveItem = {
          index: newActiveIndex,
          item: items[newActiveIndex],
        };
        setActive(newActiveItem);

        const newNext = getNextItemState(items, newActiveIndex);
        setNext(newNext);

        const newPrev = getPrevItemState(items, newActiveIndex);
        setPrev(newPrev);
      }
    },
    [hasItems, items],
  );

  const animateNextItem = useCallback(() => {
    if (next) {
      setAnimate(true);

      wait(() => {
        setNewCarouselState(next.index);
      }, 700);
    }
  }, [next, setNewCarouselState]);

  const animatePrevItem = useCallback(() => {
    if (prev) {
      setAnimatePrev(true);

      wait(() => {
        setNewCarouselState(prev.index);
      }, 700);
    }
  }, [prev, setNewCarouselState]);

  const previousItems = usePrevious(items);

  useEffect(() => {
    if (JSON.stringify(previousItems) !== JSON.stringify(items)) {
      if (active) {
        const newIndex = items.findIndex(({ id }) => id === active.item.id);

        if (newIndex > -1) {
          // active item is the same
          setNewCarouselState(newIndex);
        } else {
          // animate new active item in like next item
          setAnimate(true);
          setNewCarouselState(0);
        }
      } else {
        setNewCarouselState(0);
      }
    }
  }, [active, items, previousItems, setNewCarouselState]);

  const handleNextItem = () => {
    if (next) {
      metrics.count('nudge_next', [`id:${active?.item.id || ''}`]);
      animateNextItem();
    }
  };

  const handlePrevItem = () => {
    if (prev) {
      metrics.count('nudge_prev', [`id:${active?.item.id || ''}`]);
      animatePrevItem();
    }
  };

  const renderItem = (
    itemState?: ItemState,
    { onSkip = noop, isActive = true } = {},
  ) => {
    const multipleItems = items.length > 1;

    return (
      <CurrentItem
        {...itemState?.item}
        active={isActive || animate}
        onSkip={onSkip}
        showSkip={multipleItems}
        hidden={!(active || animate)}
        index={itemState?.index}
      />
    );
  };

  const rootStyles = clsx({
    [styles.root]: true,
    [styles.slide]: animate && hasItems,
    [styles.slideToPrev]: animatePrev && hasItems,
    [styles.fade]: animate && !hasItems,
  });

  if (!hasItems) {
    return (
      <div className={rootStyles}>
        {active && (
          <div className={styles.itemContainer}>
            <div className={styles.item}>{renderItem(active)}</div>
          </div>
        )}
      </div>
    );
  }

  return (
    <Box position="relative" id="relative_position">
      {prevButton === 'icon' &&
      items.length > 1 &&
      active &&
      active.index > 0 ? ( // Handle profile suggestions
        <Inline space="none">
          <Box
            className={styles.arrowLeft}
            background="surface"
            boxShadow="borderNeutralLight"
            display="block"
            position="absolute"
            zIndex={1}
            borderRadius="full"
            cursor="pointer"
            onClick={handlePrevItem}
            data={{
              automation: `prev-suggestion-button-${active?.item.id}-${active?.index}`,
            }}
          >
            <Box className={styles.innerArrowLeft}>
              <ButtonIcon
                id="carousel-next"
                icon={<IconChevron tone="secondary" direction="left" />}
                size="large"
                label={t('Prev')}
                variant="transparent"
                bleed={false}
              />
            </Box>
          </Box>
        </Inline>
      ) : null}
      <Stack space="medium">
        <div className={rootStyles}>
          <div className={styles.itemContainer}>
            <div className={styles.prevItem} id="prev_element">
              {renderItem(prev, { isActive: false })}
            </div>
            <div className={styles.item} id="active_element">
              {renderItem(active, { onSkip: handleNextItem })}
            </div>
            <div className={styles.item} id="next_element">
              {renderItem(next, { isActive: false })}
            </div>
          </div>
        </div>

        {items.length > 1 ? (
          <Box>
            {nextButton === 'button' ? (
              // Handle profile strength
              <Inline space="none">
                <Button
                  variant="transparent"
                  tone="neutral"
                  bleed
                  onClick={handleNextItem}
                  data={{
                    automation: `next-tip-button-${active?.item.id}-${active?.index}`,
                  }}
                  icon={<IconArrow direction="right" />}
                  iconPosition="trailing"
                >
                  {t('Next tip')}
                </Button>
              </Inline>
            ) : null}
            {nextButton === 'icon' &&
            active &&
            active.index < items.length - 1 ? ( // Handle profile suggestions
              <Box>
                <Box
                  className={styles.arrow}
                  background="surface"
                  boxShadow="borderNeutralLight"
                  display="block"
                  position="absolute"
                  zIndex={1}
                  borderRadius="full"
                  cursor="pointer"
                  onClick={handleNextItem}
                  data={{
                    automation: `next-suggestion-button-${active?.item.id}-${active?.index}`,
                  }}
                  right={0}
                  top={0}
                >
                  <Box className={styles.innerArrowRight}>
                    <ButtonIcon
                      id="carousel-next"
                      icon={<IconChevron tone="secondary" direction="right" />}
                      size="large"
                      label={t('Next')}
                      variant="transparent"
                      bleed={false}
                    />
                  </Box>
                </Box>
                <Box marginLeft="small">
                  <Inline space="none">
                    <Button
                      variant="transparent"
                      tone="neutral"
                      bleed
                      onClick={handleNextItem}
                      data={{
                        automation: `next-tip-button-${active?.item.id}-${active?.index}`,
                      }}
                      icon={<IconArrow direction="right" />}
                      iconPosition="trailing"
                    >
                      {t('Next tip')}
                    </Button>
                  </Inline>
                </Box>
              </Box>
            ) : null}
          </Box>
        ) : null}
      </Stack>
    </Box>
  );
};
